import { useState, useEffect, useRef } from 'react';
import { useConversation } from '@elevenlabs/react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import './chat-animations.css';
import CallIcon from '@mui/icons-material/Call';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import SendIcon from '@mui/icons-material/Send';
import PropTypes from 'prop-types';

/**
 * Generic ElevenLabs AI Agent UI/Logic template.
 * Props:
 * - agentId: string (required). The ElevenLabs Agent ID to connect to.
 * - title: string (required). Large title displayed at the top (e.g., "Bay Hills FLO").
 * - subtitle: string (optional). Subtitle below the title (e.g., "FAQs").
 * - suggestionCommands: Array<{ label: string, command: string }> (optional). Chips shown before connect.
 * - logoSrc: string (optional). Orb logo/image path; defaults to './bhlogo.png'.
 * - logoAlt: string (optional). Alt text for the logo image.
 * - apiBase: string (optional). API base for token endpoint; defaults to VITE_API_BASE_URL or '/api/v1'.
 * - textOnly: boolean (optional). Force text-only conversation; defaults false.
 */
const AIAgentTemplate = ({
  agentId,
  title,
  subtitle = '',
  suggestionCommands = [],
  logoSrc = './bhlogo.png',
  logoAlt = 'Start',
  apiBase,
  textOnly = false,
}) => {
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [micMuted, _setMicMuted] = useState(false);
  const [_isListening, setIsListening] = useState(false);
  const [aiSpeakingVisual, setAiSpeakingVisual] = useState(false);

  // Timers
  const keepAliveIntervalRef = useRef(null);
  const keepAliveStopTimeoutRef = useRef(null);
  const waitTimeoutRef = useRef(null);

  // Determine API base
  const apiBaseUrl = (apiBase || import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '');

  // Extract text from various message shapes
  const extractAiText = (msg) => {
    if (!msg) return '';
    if (typeof msg === 'string') return msg;
    if (typeof msg.message === 'string') return msg.message;
    if (typeof msg.delta === 'string') return msg.delta;
    if (msg.text) return msg.text;
    if (msg.content) return Array.isArray(msg.content) ? msg.content.join(' ') : msg.content;
    if (msg.transcript) return msg.transcript;
    if (msg.message && (msg.message.text || msg.message.content)) {
      return msg.message.text || msg.message.content;
    }
    if (msg.output && (msg.output.text || msg.output.content)) {
      return msg.output.text || msg.output.content;
    }
    if (msg.response && (msg.response.text || msg.response.content)) {
      return msg.response.text || msg.response.content;
    }
    try {
      const s = JSON.stringify(msg);
      return s.length < 200 ? s : '';
    } catch {
      return '';
    }
  };

  const {
    status,
    isSpeaking,
    startSession,
    endSession,
    sendUserMessage,
    sendUserActivity,
  } = useConversation({
    textOnly,
    micMuted,
    onConnect: () => {
      setIsListening(true);
      setErrorMessage('');
      try {
        if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
        if (keepAliveStopTimeoutRef.current) clearTimeout(keepAliveStopTimeoutRef.current);
        keepAliveIntervalRef.current = setInterval(() => {
          try { if (typeof sendUserActivity === 'function') sendUserActivity(); } catch (e) { /* no-op */ }
        }, 20000);
        keepAliveStopTimeoutRef.current = setTimeout(() => {
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
        }, 120000);
      } catch (e) { /* no-op */ }
    },
    onDisconnect: () => {
      setIsListening(false);
      if (keepAliveIntervalRef.current) { clearInterval(keepAliveIntervalRef.current); keepAliveIntervalRef.current = null; }
      if (keepAliveStopTimeoutRef.current) { clearTimeout(keepAliveStopTimeoutRef.current); keepAliveStopTimeoutRef.current = null; }
      if (waitTimeoutRef.current) { clearTimeout(waitTimeoutRef.current); waitTimeoutRef.current = null; }
    },
    onMessage: (msg) => {
      try {
        const text = extractAiText(msg);
        if (!text) return;
        const rawSource = msg?.source || msg?.role || 'ai';
        const source = rawSource === 'user' || rawSource === 'input' ? 'user' : 'ai';
        setMessages((prev) => [
          ...prev,
          { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, source, text },
        ]);
        setAiSpeakingVisual(true);
        setTimeout(() => setAiSpeakingVisual(false), Math.min(2400, Math.max(1200, text.length * 20)));
      } catch (e) { /* no-op */ }
    },
    onError: (err) => {
      const msg = err?.message || String(err || 'Unknown error');
      setErrorMessage(msg);
      // eslint-disable-next-line no-console
      console.error('ElevenLabs error:', err);
    },
  });

  // Cleanup timers on unmount
  useEffect(() => () => {
    if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
    if (keepAliveStopTimeoutRef.current) clearTimeout(keepAliveStopTimeoutRef.current);
    if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
  }, []);

  // Microphone permission (if not textOnly)
  useEffect(() => {
    const requestMicPermission = async () => {
      try {
        if (!textOnly) {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          setHasPermission(true);
        } else {
          setHasPermission(true);
        }
      } catch (error) {
        setErrorMessage('Microphone access denied');
        // eslint-disable-next-line no-console
        console.error('Error accessing microphone:', error);
      }
    };
    requestMicPermission();
  }, [textOnly]);

  const resolveAgentId = () => {
    return agentId || import.meta.env.VITE_ELEVENLABS_AGENT_ID || '';
  };

  const handleStartConversation = async () => {
    try {
      const resolvedAgentId = resolveAgentId();
      if (!resolvedAgentId) throw new Error('Missing agentId');
      const tokenRes = await fetch(`${apiBaseUrl}/elevenlabs/token?agentId=${encodeURIComponent(resolvedAgentId)}`);
      if (!tokenRes.ok) {
        const msg = await tokenRes.text();
        throw new Error(msg || 'Failed to fetch conversation token');
      }
      const conversationToken = await tokenRes.text();
      await startSession({ conversationToken, connectionType: 'webrtc', agentId: resolvedAgentId });
    } catch (error) {
      setErrorMessage('Failed to start conversation: ' + (error.message || error));
      // eslint-disable-next-line no-console
      console.error('Error starting conversation:', error);
    }
  };

  const handleEndConversation = async () => {
    try { await endSession(); } catch (error) {
      setErrorMessage('Failed to end conversation');
      // eslint-disable-next-line no-console
      console.error('Error ending conversation:', error);
    }
  };

  const handleSendText = async () => {
    const value = textInput.trim();
    if (!value) return;
    const parseWaitMs = (raw) => {
      const s = raw.toLowerCase().trim();
      if (!s.startsWith('wait')) return null;
      const rest = s.replace(/^wait\s*/, '');
      let m = rest.match(/^(\d+)\s+(\d+)\s*seconds?/);
      if (m) { const minutes = parseInt(m[1], 10); const seconds = parseInt(m[2], 10); return Math.min((minutes * 60 + seconds) * 1000, 120000); }
      m = rest.match(/^(\d+):(\d+)\b/);
      if (m) { const minutes = parseInt(m[1], 10); const seconds = parseInt(m[2], 10); return Math.min((minutes * 60 + seconds) * 1000, 120000); }
      m = rest.match(/^(\d+)\s*(minutes?|mins?|m)\b/);
      if (m) { const minutes = parseInt(m[1], 10); return Math.min(minutes * 60 * 1000, 120000); }
      m = rest.match(/^(\d+)\s*(seconds?|secs?|s)?\b/);
      if (m) { const seconds = parseInt(m[1], 10); return Math.min(seconds * 1000, 120000); }
      return null;
    };
    const waitMs = parseWaitMs(value);

    setMessages((prev) => [ ...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, source: 'user', text: value } ]);
    setTextInput('');

    try {
      if (status !== 'connected') {
        const resolvedAgentId = resolveAgentId();
        if (!resolvedAgentId) throw new Error('Missing agentId');
        const tokenRes = await fetch(`${apiBaseUrl}/elevenlabs/token?agentId=${encodeURIComponent(resolvedAgentId)}`);
        if (!tokenRes.ok) { const msg = await tokenRes.text(); throw new Error(msg || 'Failed to fetch conversation token'); }
        const conversationToken = await tokenRes.text();
        await startSession({ conversationToken, connectionType: 'webrtc', agentId: resolvedAgentId });
      }
      if (waitMs !== null) {
        if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
  waitTimeoutRef.current = setTimeout(() => { try { if (typeof sendUserMessage === 'function') sendUserMessage('Please ask the last question again.'); } catch (e) { /* no-op */ } }, waitMs);
      } else {
        if (typeof sendUserMessage === 'function') sendUserMessage(value);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to send message:', err);
      setErrorMessage('Failed to send message');
    }
  };

  // Input focus controls chat expansion
  // input focus not used directly; rely on chatOpen to control visibility
  const [chatOpen, setChatOpen] = useState(false);

  // Close chat when clicking outside while disconnected
  const chatRef = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      const isDisconnected = status !== 'connected';
      if (!isDisconnected) return;
      if (!chatOpen) return;
      if (!chatRef.current) return;
      if (!chatRef.current.contains(e.target)) {
        setChatOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [status, chatOpen]);

  const inputRef = useRef(null);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 160; // ~10 lines
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  }, [textInput]);

  return (
    <div className="min-h-screen w-full bg-transparent relative overflow-visible">

      {/* Back Button */}
      <button onClick={() => navigate('/')} className="absolute top-4 left-4 z-20 w-12 h-12 bg-white/20 backdrop-blur-md border border-white/30 rounded-full hover:bg-white/30 hover:border-white/40 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center" style={{ backdropFilter: 'blur(10px) saturate(180%)' }}>
        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="absolute top-12 left-1/2 -translate-x-1/2 text-center z-20">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-700 via-gray-600 to-stone-600 bg-clip-text text-transparent">{title}</h1>
          {subtitle ? (<p className="text-base sm:text-lg text-gray-600/80 font-medium">{subtitle}</p>) : null}
        </div>

        {/* Center control */}
        <div className={cn('relative z-10 flex flex-col items-center justify-center min-h-[40vh]')}>
          <button
            onClick={status === 'connected' ? handleEndConversation : handleStartConversation}
            className={cn(
              'group relative w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden shadow-md border border-gray-200 bg-white transition-transform duration-200 hover:scale-105',
              (isSpeaking || aiSpeakingVisual) ? 'orb-speaking orb-ripple' : ''
            )}
            style={{}}
            aria-label={status === 'connected' ? 'Stop' : 'Start'}
          >
            <div className="absolute inset-0" />
            <img src={logoSrc} alt={logoAlt} className="relative z-10 w-24 h-24 sm:w-26 sm:h-26 object-contain m-auto translate-y-1 group-hover:translate-y-0 transition-transform duration-300" />
          </button>
          <div className="mt-4 text-gray-600 text-sm">{status !== 'connected' ? 'Tap to start' : isSpeaking ? 'Speaking…' : 'Connected'}</div>

          {/* Suggestion chips */}
          {status !== 'connected' && suggestionCommands?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2 justify-center shadow-xl rounded-full border-t">
              {suggestionCommands.map(({ label, command }) => (
                <button
                  key={label}
                  onClick={async () => {
                    const cmd = command;
                    if (cmd) {
                      try {
                        if (status !== 'connected') {
                          await handleStartConversation();
                        }
                        setMessages((prev) => [ ...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, source: 'user', text: cmd } ]);
                        if (typeof sendUserMessage === 'function') {
                          sendUserMessage(cmd);
                        }
                        setTextInput('');
                        return;
                      } catch (e) {
                        setErrorMessage(`Failed to start ${label.toLowerCase()}`);
                        return;
                      }
                    }
                    if (status !== 'connected') await handleStartConversation();
                    setTextInput(label);
                  }}
                  className="px-3 py-1.5 rounded-full text-xs border border-white/40 bg-white/40 text-gray-700 backdrop-blur-xl hover:bg-white/60 transition"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bottom chat + input */}
        <div className="fixed inset-x-0 bottom-4 sm:bottom-6 lg:bottom-8 z-30 px-4">
          <div className="mx-auto w-full max-w-md sm:max-w-lg lg:max-w-xl space-y-3">
      {chatOpen && (
              <div className={cn('rounded-xl p-2 overflow-visible shadow-sm border border-gray-200 transition-all duration-200 bg-transparent')}>
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[10px] text-gray-600 text-center">Start chatting by typing a message below.</p>
                  </div>
                ) : (
                  <div
        className="flex flex-col justify-end space-y-2 min-h-[140px] max-h-[65vh] overflow-y-auto overscroll-contain scroll-smooth pr-1 -mr-1"
                    id="chat-scroll-container"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    {messages.map((m, index) => (
                      <div key={m.id} className={cn('flex slide-in-animation', m.source === 'user' ? 'justify-end' : 'justify-start')} style={{ animationDelay: `${index * 0.05}s` }}>
                        <div className={cn('px-2 py-1 text-[9px] max-w-[85%] rounded-xl transform transition-all duration-200', m.source === 'user' ? 'text-white bg-gray-900' : 'text-gray-800 bg-gray-100 border border-gray-200')}>
                          <span className="block opacity-60 text-[8px] mb-0.5 uppercase tracking-wide">{m.source === 'user' ? 'You' : m.source === 'ai' ? 'AI' : 'System'}</span>
                          <span className="leading-tight whitespace-pre-wrap break-words">{m.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

      <div ref={chatRef} className={cn('relative flex items-end gap-2 rounded-xl p-2 shadow-sm border border-gray-200 bg-transparent', status === 'connected' && !isSpeaking ? 'ring-1 ring-emerald-300/70' : '')}>
              <textarea
                value={textInput}
        onChange={(e) => { setTextInput(e.target.value); setChatOpen(true); try { if (typeof sendUserActivity === 'function') sendUserActivity(); } catch (e) { /* no-op */ } }}
                onFocus={() => { setChatOpen(true); }}
                onBlur={() => { /* keep state */ }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendText(); } }}
                placeholder={status === 'connected' ? 'Type your message…' : 'Type to start and connect…'}
                className="flex-1 bg-transparent outline-none text-[11px] text-gray-800 placeholder:text-gray-400 px-3 py-2 pr-[22%] min-h-[40px] max-h-40 overflow-y-auto"
                ref={inputRef}
                rows={1}
                style={{ resize: 'none' }}
              />
              <div className="absolute inset-y-2 right-2 w-[20%] flex items-end justify-end gap-2">
                {status === 'connected' ? (
                  <button onClick={handleEndConversation} className="px-3 py-2 rounded-xl transition bg-red-100 text-red-700 hover:bg-red-200 border border-red-300" title="Disconnect" aria-label="Disconnect">
                    <PhoneDisabledIcon fontSize="small" />
                  </button>
                ) : (
                  <button onClick={handleStartConversation} className="px-3 py-2 rounded-xl transition bg-emerald-600 text-white hover:bg-emerald-500" title="Connect" aria-label="Connect">
                    <CallIcon fontSize="small" />
                  </button>
                )}
                <button onClick={handleSendText} disabled={!textInput.trim()} className={cn('p-2 rounded-xl transition', textInput.trim() ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-200 text-gray-500 cursor-not-allowed')} title="Send" aria-label="Send">
                  <SendIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Status Indicators */}
        <div className="flex flex-col items-center space-y-4 w-full max-w-md relative z-10">
          {errorMessage && (
            <div className="bg-red-50/60 backdrop-blur-lg border border-red-200/40 rounded-xl px-6 py-3 shadow-lg" style={{ backdropFilter: 'blur(15px) saturate(180%)' }}>
              <p className="text-sm sm:text-base text-red-700">{errorMessage}</p>
            </div>
          )}
          {!hasPermission && !textOnly && (
            <div className="bg-amber-50/60 backdrop-blur-lg border border-amber-200/40 rounded-xl px-6 py-3 text-center shadow-lg" style={{ backdropFilter: 'blur(15px) saturate(180%)' }}>
              <p className="text-sm sm:text-base text-amber-700">Please allow microphone access to start</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAgentTemplate;

AIAgentTemplate.propTypes = {
  agentId: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  suggestionCommands: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      command: PropTypes.string.isRequired,
    })
  ),
  logoSrc: PropTypes.string,
  logoAlt: PropTypes.string,
  apiBase: PropTypes.string,
  textOnly: PropTypes.bool,
};

